import { type DependencyList, useCallback, useEffect, useState } from 'react';

export interface AsyncValue<T> {
    data: T;
    isLoading: boolean;
    isLoaded: boolean;
    isError: boolean;
    refetch: () => void;
}

/**
 * Адаптер «промис → React-стейт» с отменой на размонтировании и ручным refetch.
 * Не кэширует и не дедуплицирует запросы — для общего состояния между компонентами
 * нужен полноценный стор/query-кэш.
 */
export function useAsyncValue<T>(factory: () => Promise<T>, initialData: T, deps: DependencyList): AsyncValue<T> {
    const [data, setData] = useState<T>(initialData);
    const [isLoading, setIsLoading] = useState(true);
    const [isLoaded, setIsLoaded] = useState(false);
    const [isError, setIsError] = useState(false);
    const [nonce, setNonce] = useState(0);

    const refetch = useCallback(() => setNonce((value) => value + 1), []);

    useEffect(() => {
        let cancelled = false;

        setIsLoading(true);
        setIsLoaded(false);
        setIsError(false);

        factory()
            .then((result) => {
                if (!cancelled) {
                    setData(result);
                    setIsLoading(false);
                    setIsLoaded(true);
                }
            })
            .catch(() => {
                if (!cancelled) {
                    setIsLoading(false);
                    setIsLoaded(true);
                    setIsError(true);
                }
            });

        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [...deps, nonce]);

    return { data, isLoading, isLoaded, isError, refetch };
}
